import {
  ChangeDetectionStrategy,
  Component,
  effect,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';

import { form, FormField as SignalFormField, required } from '@angular/forms/signals';

import {
  SignalCheckbox,
  SignalDateField, SignalFormDirective,
  SignalTextField,
  Weekday,
} from '@vallift/ngl';

import type { FormField } from '../../../../models/built-form';
interface DateRangeConfigModel {
  label: string;
  helpText: string;
  defaultStartDate: Date | null;
  defaultEndDate: Date | null;
  minDate: Date | null;
  maxDate: Date | null;
  allowOnlyPast: boolean;
  allowOnlyFuture: boolean;
  required: boolean;
}

@Component({
  selector: 'app-date-range-picker-config-view',
  standalone: true,
  imports: [SignalFormField, SignalTextField, SignalDateField, SignalCheckbox, SignalFormDirective],
  templateUrl: './date-range-config-view.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DateRangeConfigViewComponent {
  readonly field = input.required<FormField>();
  readonly fieldChange = output<FormField>();

  readonly model = signal<DateRangeConfigModel>({
    label: '',
    helpText: '',
    defaultStartDate: null,
    defaultEndDate: null,
    minDate: null,
    maxDate: null,
    allowOnlyPast: false,
    allowOnlyFuture: false,
    required: false,
  });

  readonly configForm = form(this.model,(schema) => {
    required(schema.label);
  });

  private lastFieldId: string | null = null;
  private suppressEmit = false;

  constructor() {
    effect(() => {
      const next = this.field();
      if (next.id === this.lastFieldId) {
        return;
      }
      this.lastFieldId = next.id;
      this.suppressEmit = true;

      this.model.set({
        label: next.label,
        helpText: next.helpText ?? '',
        defaultStartDate: next.startDate ?? null,
        defaultEndDate: next.endDate ?? null,
        minDate: next.minDate ?? null,
        maxDate: next.maxDate ?? null,
        allowOnlyPast: next.dateRangeAllowOnlyPast ?? false,
        allowOnlyFuture: next.dateRangeAllowOnlyFuture ?? false,
        required: next.required ?? false,
      });
    });

    effect(() => {
      const draft = this.model();
      if (this.suppressEmit) {
        this.suppressEmit = false;
        return;
      }
      const field = untracked(() => this.field());
      if (!field || field.id !== this.lastFieldId) {
        return;
      }
      this.fieldChange.emit({
        ...field,
        label: draft.label.trim(),
        helpText: draft.helpText.trim() || undefined,
        startDate: draft.defaultStartDate,
        endDate: draft.defaultEndDate,
        minDate: draft.minDate,
        maxDate: draft.maxDate,
        dateRangeAllowOnlyPast: draft.allowOnlyPast,
        dateRangeAllowOnlyFuture: draft.allowOnlyFuture,
        required: draft.required,
        options: undefined,
        headingLevel: undefined,
        spacerSize: undefined,
      });
    });
  }
}
